import React, { useEffect, useState } from "react";
import {
  TextField,
  Stack,
  Button,
  Container,
  Box,
  Typography,
  Autocomplete,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { dbPlayerType, getPlayers, getTeamById } from "../../api/api";
import { useNavigate, useParams } from "react-router-dom";
import { ROLES, TeamType } from "../../types/types";
import NotificationService from "../../components/notification/NotificationService";
import { getGuid } from "../../utils";
import ballLogo from "../../assets/icons/ball-logo.png";
import { useAppSelector } from "../../hooks";
import { supabase } from "../../supabase";

const MAX_FIELDS = 9;

type dbPlayerUIType = dbPlayerType & { birthday?: string; user?: string | null };

type User = {
  id: string;
  email: string | null;
  role: string;
  firstname?: string | null;
  lastname?: string | null;
  number?: string | null;
  birthday?: string | null; // now YYYY-MM-DD string
  fullName?: string;
};

const StyledTextField: React.FC<any> = (props) => (
  <TextField
    {...props}
    variant={props.variant || "filled"}
    InputProps={{
      style: props.disabled
        ? { color: "white", fontStyle: "italic", fontWeight: "bold" }
        : { color: "white" },
      disableUnderline: true,
    }}
    sx={{
      "& .MuiInputBase-input.Mui-disabled": {
        color: "white",
        fontStyle: "italic",
        fontWeight: "bold",
        borderRadius: 1,
        border: "1px solid #ccc",
        WebkitTextFillColor: "white",
      },
      "& .MuiInputLabel-root.Mui-disabled": {
        color: "white",
        fontStyle: "italic",
        fontWeight: "bold",
      },
      ...props.sx,
    }}
  />
);

const DynamicInputList: React.FC = () => {
  const { id: teamId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const tournament = useAppSelector((state) => state.tournament.tournament);
  const isAdmin = useAppSelector((state) => state.app.admin);

  const [inputs, setInputs] = useState<dbPlayerUIType[]>([]);
  const [originalInputs, setOriginalInputs] = useState<dbPlayerUIType[]>([]);
  const [errors, setErrors] = useState<boolean[]>([]);
  const [team, setTeam] = useState<TeamType>();
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [autoFillFromProfile, setAutoFillFromProfile] = useState<boolean>(true);

  const group = useAppSelector((state) => state.groups.groups).find(
    (g) => g.id === (team?.group as any as string)
  );

  const lockDate = tournament?.date - 24 * 60 * 60;
  const isLocked = Date.now() / 1000 >= lockDate && !isAdmin;

  const sortPlayers = (arr: dbPlayerUIType[]) =>
    [...arr].sort((a, b) => {
      if (a.number && b.number) return parseInt(a.number) - parseInt(b.number);
      if (a.number && !b.number) return -1;
      if (!a.number && b.number) return 1;
      return 0;
    });

  // Fetch players
  useEffect(() => {
    if (!teamId || !tournament?.id) return;

    getPlayers(teamId).then((response) => {
      const sortedPlayers = response ?? [];
      const existingInputs: dbPlayerUIType[] = sortedPlayers.map((p) => ({
        ...p,
        birthday: p.birthday ?? "",
        user: (p as any).user ?? null,
      }));

      while (existingInputs.length < MAX_FIELDS) {
        existingInputs.push({
          id: getGuid(),
          name: "",
          number: "",
          birthday: "",
          team: teamId,
          tournament: tournament.id,
          user: null,
        });
      }

      setInputs(sortPlayers(existingInputs));
      setOriginalInputs(sortPlayers(existingInputs));
      setErrors(existingInputs.map(() => false));
    });
  }, [teamId, tournament?.id]);

  // Fetch team
  useEffect(() => {
    if (!teamId) return;

    getTeamById(teamId).then(setTeam).catch(() => navigate("error"));
  }, [teamId, navigate]);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      if (!team?.club?.id) {
        setLoadingUsers(false);
        return;
      }
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("role", ROLES.PLAYER)
        .eq("club", team?.club?.id);

      if (!error && data) {
        const withFullName = data.map((u: User) => ({
          id: u.id,
          email: u.email,
          role: u.role,
          firstname: u.firstname,
          lastname: u.lastname,
          number: u.number,
          birthday: u.birthday ?? "",
          fullName: `${u.firstname ?? ""} ${u.lastname ?? ""}`.trim(),
        }));
        setUsers(withFullName);
      }
      setLoadingUsers(false);
    };
    fetchUsers();
  }, [team?.club?.id]);

  // Handle input changes
  const handleInputChange = (
    index: number,
    field: keyof dbPlayerUIType,
    value: any
  ) => {
    setInputs((prev) => {
      return prev.map((row, i) => {
        if (i !== index) return row;

        if (field === "user") {
          if (value) {
            const selectedUser = users.find((u) => u.id === value);
            if (selectedUser) {
              if (autoFillFromProfile) {
                return {
                  ...row,
                  user: selectedUser.id,
                  name: selectedUser.fullName ?? "",
                  number: selectedUser.number ?? "",
                  birthday: selectedUser.birthday ?? "",
                };
              } else {
                return {
                  ...row,
                  user: selectedUser.id,
                };
              }
            }
          }
          return { ...row, user: null };
        }

        // Validér navnelængde (max 35 karakterer)
        if (field === "name" && value.length > 35) {
          return row;
        }

        return { ...row, [field]: value };
      });
    });
  };

  const handleClearRow = (index: number) => {
    setInputs((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, name: "", number: "", birthday: "", user: null } : row
      )
    );
    setErrors((prev) => prev.map((e, i) => (i === index ? false : e)));
  };

  const hasChanges =
    inputs.length !== originalInputs.length ||
    inputs.some((p, i) => {
      const o = originalInputs[i];
      return (
        p.name !== o.name ||
        p.number !== o.number ||
        p.birthday !== o.birthday ||
        (p.user ?? null) !== (o.user ?? null)
      );
    });

  const handleSubmit = async () => {
    if (!teamId) return;

    const newErrors: boolean[] = inputs.map(
      (input) =>
        Boolean(
          ((input.name?.trim() || input.number?.trim()) &&
            (!input.name?.trim() || !input.number?.trim())) ||
          (input.name && input.name.length > 35)
        )
    );
    setErrors(newErrors);
    if (newErrors.some((e) => e)) {
      const hasLongName = inputs.some((input) => input.name && input.name.length > 35);
      if (hasLongName) {
        NotificationService.showToast({
          severity: "error",
          text: "Fornavn og efternavn må maks være 35 karakterer i alt.",
          duration: 3000,
        });
      }
      return;
    }

    const rowsToDelete = inputs.filter((p) => !p.name && !p.number && !p.user).map((p) => p.id);
    const rowsToUpsert: dbPlayerType[] = inputs
      .filter((p) => p.name || p.number || p.user)
      .map((p) => ({
        id: p.id || getGuid(),
        name: p.name,
        number: p.number,
        birthday: p.birthday ? p.birthday : null,
        team: teamId,
        tournament: tournament!.id,
        user: p.user || null,
      }));

    try {
      if (rowsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from("players")
          .delete()
          .in("id", rowsToDelete);
        if (deleteError) throw deleteError;
      }

      const { data, error } = await supabase
        .from("players")
        .upsert(rowsToUpsert, { onConflict: "id" })
        .select();
      if (error) throw error;

      const mergedInputs: dbPlayerUIType[] = (data as any).map((p: any) => ({
        ...p,
        birthday: p.birthday ?? "",
        user: p.user ?? null,
      }));

      while (mergedInputs.length < MAX_FIELDS) {
        mergedInputs.push({
          id: getGuid(),
          name: "",
          number: "",
          birthday: "",
          team: teamId,
          tournament: tournament!.id,
          user: null,
        });
      }

      setInputs(sortPlayers(mergedInputs));
      setOriginalInputs(sortPlayers(mergedInputs));
      setErrors(mergedInputs.map(() => false));

      NotificationService.showToast({
        severity: "success",
        text: "Holdkortet blev opdateret.",
        duration: 3000,
      });
    } catch (err) {
      console.error(err);
      NotificationService.showToast({
        severity: "error",
        text: "Der skete en uventet fejl. Prøv igen senere, eller kontakt turneringsarrangøren.",
        duration: 3000,
      });
    }
  };

  return (
    <div className="edit-team-list-page">
      <Container maxWidth="xl">
        <Stack flexDirection="row" justifyContent="center" alignItems="center" paddingBottom="50px">
          <img src={team?.club?.logo ?? ballLogo} height={50} width={50} style={{ marginRight: 20 }} />
          <Typography variant="h3" sx={{ color: "white", textAlign: "center" }}>
            {team?.teamName} - {group?.pulje.puljeName ?? "-"}
          </Typography>
          <img src={team?.club?.logo ?? ballLogo} height={50} width={50} style={{ marginLeft: 20 }} />
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={autoFillFromProfile}
                onChange={(e) => setAutoFillFromProfile(e.target.checked)}
                disabled={isLocked}
                sx={{
                  color: "white",
                  '&.Mui-checked': {
                    color: "#1976d2",
                  },
                }}
              />
            }
            label="Autoudfyld fra brugerprofil"
            sx={{
              color: "white",
              '& .MuiFormControlLabel-label': {
                fontSize: "1rem",
              },
            }}
          />
        </Box>

        <Box sx={{ overflowX: "auto", width: "100%", display: "flex", justifyContent: { xs: "flex-start", md: "center" } }}>
          <Stack spacing={2} sx={{ minWidth: 850 }}>
            {inputs.map((input, index) => (
              <Stack key={input.id} direction="row" spacing={2} justifyContent="center" alignItems="center">
                <StyledTextField
                  sx={{ width: 80 }}
                  label="No."
                  type="number"
                  value={input.number}
                  onChange={(e: any) => handleInputChange(index, "number", e.target.value)}
                  inputProps={{ min: 1 }}
                  error={errors[index]}
                  disabled={isLocked}
                />

                <StyledTextField
                  sx={{ width: 200 }}
                  label="Name"
                  value={input.name}
                  onChange={(e: any) => handleInputChange(index, "name", e.target.value)}
                  error={errors[index]}
                  disabled={isLocked}
                />

                <StyledTextField
                  sx={{ width: 140 }}
                  type="date"
                  value={input.birthday}
                  onChange={(e: any) => handleInputChange(index, "birthday", e.target.value)}
                  error={false}
                  disabled={isLocked}
                />

                <Box sx={{ width: 260 }}>
                  <Autocomplete
                    disabled={isLocked}
                    options={users
                      .filter(u => !inputs.some(r => r.user === u.id && r !== input))
                      .sort((a, b) => {
                        const numA = a.number ? parseInt(a.number) : Infinity;
                        const numB = b.number ? parseInt(b.number) : Infinity;
                        return numA - numB;
                      })
                    }
                    getOptionLabel={(u) => u.email || "-"}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    value={users.find((u) => u.id === input.user) ?? null}
                    onChange={(e, newValue) => handleInputChange(index, "user", newValue?.id ?? null)}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start !important',
                        padding: '8px 16px !important',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08) !important',
                        }
                      }}>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#1976d2', whiteSpace: 'nowrap' }}>
                          {option.number ? `${option.number}. ` : ""}
                          {option.fullName || "-"}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {option.email || "-"}
                        </Typography>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Brugerprofil"
                        placeholder="Vælg bruger"
                        sx={{
                          input: { color: 'white' },
                          '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                          '& .MuiInputLabel-root.Mui-focused': { color: '#1976d2' },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                            '&.Mui-focused fieldset': { borderColor: '#1976d2' },
                          }
                        }}
                      />
                    )}
                    sx={{
                      '& .MuiAutocomplete-paper': {
                        backgroundColor: '#fff',
                      },
                      '& .MuiAutocomplete-listbox': {
                        padding: '4px 0',
                      }
                    }}
                  />
                </Box>

                <Tooltip title={isLocked ? "Holdkortet er låst" : "Slet række"}>
                  <span>
                    <DeleteOutlineIcon
                      color="error"
                      onClick={() => !isLocked && handleClearRow(index)}
                      sx={{ cursor: isLocked ? "not-allowed" : "pointer" }}
                    />
                  </span>
                </Tooltip>
              </Stack>
            ))}
          </Stack>
        </Box>

        <Box display="flex" justifyContent="center" marginTop="30px">
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLocked || !hasChanges}
            sx={{
              backgroundColor: "#1976d2",
              color: "white",
              "&.Mui-disabled": { backgroundColor: "#555", color: "#ccc" },
            }}
          >
            {isLocked ? "Holdkortet er låst" : hasChanges ? "Gem ændringer" : "Gemt!"}
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default DynamicInputList;
